import { useState, type FormEvent } from "react";
import { useListCompanies, useCreateCompany, getListCompaniesQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "wouter";
import { Plus, Search, Building2, Trash2 } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useToast } from "@/hooks/use-toast";
import { useCohort } from "@/hooks/use-cohort";

const stageBadgeStyles: Record<string, string> = {
  pipeline: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  onboarding: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  exited: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
};

export default function Companies({ cohortSlug }: { cohortSlug?: string } = {}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { cohortId } = useCohort(cohortSlug);

  const listParams = {
    status: statusFilter !== "all" ? statusFilter as "pipeline" | "onboarding" | "active" | "exited" : undefined,
    search: search || undefined,
    cohortId,
  };
  const { data: companies, isLoading } = useListCompanies(listParams, {
    query: { enabled: !cohortSlug || !!cohortId, queryKey: getListCompaniesQueryKey(listParams) },
  });

  const BASE = import.meta.env.BASE_URL;
  const createMutation = useCreateCompany();

  const handleDeleteCompany = async (id: number, name: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) return;
    const res = await fetch(`${BASE}api/companies/${id}`, { method: "DELETE" });
    if (res.ok) {
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() });
      toast({ title: "Company deleted" });
    } else {
      toast({ title: "Failed to delete company", variant: "destructive" });
    }
  };

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    createMutation.mutate(
      {
        data: {
          name: form.get("name") as string,
          description: form.get("description") as string,
          sector: form.get("sector") as string,
          stage: (form.get("stage") as string) as "pipeline" | "onboarding" | "active" | "exited",
          website: (form.get("website") as string) || null,
          location: (form.get("location") as string) || null,
          thesis: (form.get("thesis") as string) || null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() });
          setDialogOpen(false);
          toast({ title: "Company created", description: "Successfully added to portfolio" });
        },
      }
    );
  };

  return (
    <div className="space-y-6" data-testid="page-companies">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Companies</h1>
            <InfoTip content="Your portfolio directory. Every company Favcy works with lives here — from early pipeline prospects to active portfolio companies. Click any company to see its full profile with updates, KPIs, team, documents, media, access controls, and audit history." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Portfolio company directory</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-company"><Plus className="w-4 h-4 mr-2" />Add Company</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif">Add Company</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input name="name" required data-testid="input-company-name" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea name="description" required data-testid="input-company-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Input name="sector" required data-testid="input-company-sector" />
                </div>
                <div className="space-y-2">
                  <Label>Stage</Label>
                  <select name="stage" className="w-full h-9 rounded-md border bg-background px-3 text-sm" defaultValue="pipeline" data-testid="select-company-stage">
                    <option value="pipeline">Pipeline</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="active">Active</option>
                    <option value="exited">Exited</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input name="website" data-testid="input-company-website" />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input name="location" data-testid="input-company-location" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Investment Thesis</Label>
                <Textarea name="thesis" data-testid="input-company-thesis" />
              </div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending} data-testid="button-submit-company">
                {createMutation.isPending ? "Creating..." : "Create Company"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search companies..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="input-search-companies" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="select-filter-status">
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            <SelectItem value="pipeline">Pipeline</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="exited">Exited</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : companies && companies.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {companies.map((company) => (
            <Link key={company.id} href={cohortSlug ? `/c/${cohortSlug}/companies/${company.id}` : `/companies/${company.id}`}>
              <Card className="cursor-pointer hover:border-primary/30 transition-colors" data-testid={`card-company-${company.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-muted rounded-md">
                        <Building2 className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-medium">{company.name}</h3>
                        <p className="text-xs text-muted-foreground">{company.sector} {company.location ? `| ${company.location}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${stageBadgeStyles[company.stage] || ""}`} data-testid={`badge-stage-${company.id}`}>
                        {company.stage}
                      </Badge>
                      <button
                        onClick={(e) => handleDeleteCompany(company.id, company.name, e)}
                        className="p-1.5 rounded-md text-muted-foreground/40 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{company.description}</p>
                  {company.equityPercent && (
                    <p className="text-xs text-muted-foreground mt-2">Equity: {company.equityPercent}%</p>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No companies found</p>
            <p className="text-sm text-muted-foreground mt-1">Add your first portfolio company to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
